/**
 * Session bootstrap — SSO через cookie Rover (same-origin, nginx проксирует /api к cloud).
 *
 * GET /api/v1/session отдаёт контекст пользователя, доступные точки и короткоживущий
 * socket_token (Phoenix.Token): cookie нельзя положить в connect-параметры WebSocket,
 * поэтому сокет ходит с этим токеном, а REST — с cookie (или dev-bearer из env).
 *
 * Состояния: ok (работаем) · unauthorized (нет сессии Rover → экран входа) ·
 * forbidden (сессия есть, раздел Vision не выдан) · error (бэкенд недоступен —
 * не блокируем UI, клиент сам деградирует в мок/поллинг).
 */
import { config } from "@/api/config";

export interface SessionUser {
  id: string;
  name: string;
}

export interface SessionRestaurant {
  id: string;
  name: string;
}

export type SessionState =
  | {
      status: "ok";
      role: string;
      user: SessionUser | null;
      restaurants: SessionRestaurant[];
    }
  | { status: "unauthorized" }
  | { status: "forbidden" }
  | { status: "error" };

interface SessionResponse {
  role: string;
  user: SessionUser | null;
  restaurants: SessionRestaurant[];
  socket_token: string;
  socket_token_ttl: number;
}

// Токен для WebSocket, полученный из /session. Хуки читают его лениво на каждом
// connect (params-функция), поэтому обновление сессии подхватывается без ремоунта.
let socketToken: string | null = null;

/** Токен для connect-параметров сокета: из сессии, иначе dev-токен из env. */
export function getSocketToken(): string | null {
  return socketToken ?? (config.userToken || null);
}

export async function fetchSession(): Promise<SessionState> {
  if (config.useMocks) {
    return { status: "ok", role: "mock", user: null, restaurants: [] };
  }

  const base = config.apiBase.replace(/\/$/, "");
  const url = new URL(`${base}/session`, window.location.origin);
  const headers: Record<string, string> = { Accept: "application/json" };
  if (config.userToken) headers["Authorization"] = `Bearer ${config.userToken}`;

  let res: Response;
  try {
    // same-origin: cookie Rover уходит автоматически (nginx-прокси = один origin).
    res = await fetch(url.toString(), { headers, credentials: "same-origin" });
  } catch {
    return { status: "error" };
  }

  if (res.status === 401) return { status: "unauthorized" };
  if (res.status === 403) return { status: "forbidden" };
  if (!res.ok) return { status: "error" };

  try {
    const body = (await res.json()) as SessionResponse;
    socketToken = body.socket_token;
    return {
      status: "ok",
      role: body.role,
      user: body.user,
      restaurants: body.restaurants ?? [],
    };
  } catch {
    return { status: "error" };
  }
}

/**
 * URL входа: дашборд живёт на vision.<родительский домен>, логин — в Rover на
 * родительском домене (он и ставит кросс-доменную cookie). Вне этой схемы
 * (localhost) возвращаем null — экран входа покажет подсказку про dev-токен.
 */
export function roverLoginUrl(): string | null {
  const { hostname, protocol } = window.location;
  if (!hostname.startsWith("vision.")) return null;
  return `${protocol}//${hostname.slice("vision.".length)}/app/`;
}
